import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export default function Tapentadol100mg() {
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);
  const [cartCount, setCartCount] = useState(0);
  const [shippingType, setShippingType] = useState<'overseas' | 'us'>('overseas');
  const [selectedPackage, setSelectedPackage] = useState('100pills');

  const product = {
    id: 6,
    name: 'Tapentadol 100mg',
    category: 'Pain Relief',
    image: 'https://public.readdy.ai/ai/img_res/b4b8ecec585c2b1bc0332d284f07c28e.jpg',
    rating: 4.8,
    reviews: 198,
    inStock: true,
    description: 'Tapentadol 100mg is a powerful pain reliever used to treat moderate to severe acute pain. It works through two mechanisms: as an opioid pain reliever and by affecting norepinephrine reuptake, providing effective pain management for various conditions.',
    activeIngredient: 'Tapentadol 100mg',
    manufacturer: 'Janssen Pharmaceuticals',
    dosage: '100mg tablets',
    packaging: '30 tablets per bottle'
  };

  const packagesOverseas = {
    '100pills': { pills: 100, price: 300 },
    '200pills': { pills: 200, price: 550 },
    '300pills': { pills: 300, price: 800 }
  };

  const packagesUS = {
    '180pills': { pills: 180, price: 350 },
    '360pills': { pills: 360, price: 650 },
    '540pills': { pills: 540, price: 950 }
  };

  const currentPackages = shippingType === 'overseas' ? packagesOverseas : packagesUS;

  useEffect(() => {
    const firstPackageKey = Object.keys(currentPackages)[0];
    setSelectedPackage(firstPackageKey);
  }, [shippingType]);

  useEffect(() => {
    const updateCartCount = () => {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        const cart = JSON.parse(savedCart);
        const count = cart.reduce((sum: number, item: any) => sum + item.quantity, 0);
        setCartCount(count);
      }
    };
    
    updateCartCount();
    window.addEventListener('storage', updateCartCount);
    return () => window.removeEventListener('storage', updateCartCount);
  }, []);

  const addToCart = () => {
    try {
      const selectedPkg = currentPackages[selectedPackage as keyof typeof currentPackages];
      const savedCart = localStorage.getItem('cart');
      const cart = savedCart ? JSON.parse(savedCart) : [];
      
      cart.push({
        id: `${product.id}-${selectedPackage}-${shippingType}`,
        name: `${product.name} - ${selectedPkg.pills} pills (${shippingType === 'overseas' ? 'Overseas' : 'US to US'})`,
        category: product.category,
        price: selectedPkg.price,
        image: product.image,
        quantity: quantity,
        shippingType: shippingType
      });
      
      localStorage.setItem('cart', JSON.stringify(cart));
      setCartCount(cart.reduce((sum: number, item: any) => sum + item.quantity, 0));
      navigate('/cart');
    } catch (error) {
      console.error('Error adding item to cart:', error);
      alert('Failed to add item to cart. Please try again.');
    }
  };

  const relatedProducts = [
    {
      id: 5,
      name: 'Soma 350mg',
      price: 125.99,
      image: 'https://readdy.ai/api/search-image?query=professional%20soma%20carisoprodol%20muscle%20relaxer%20tablets%20in%20pharmaceutical%20packaging%20on%20clean%20white%20surface%20medical%20product%20photography%20soft%20lighting%20high%20quality%20shot&width=300&height=300&seq=5&orientation=squarish',
      link: '/products/soma-350mg'
    },
    {
      id: 9,
      name: 'Ibuprofen 400mg',
      price: 125.99,
      image: 'https://readdy.ai/api/search-image?query=professional%20ibuprofen%20pain%20relief%20tablets%20in%20pharmaceutical%20blister%20pack%20on%20clean%20white%20background%20medical%20product%20photography%20soft%20lighting%20high%20quality%20shot&width=300&height=300&seq=ibu1&orientation=squarish',
      link: '/products/ibuprofen'
    },
    {
      id: 7,
      name: 'Zolpidem 10mg',
      price: 125.99,
      image: 'https://readdy.ai/api/search-image?query=professional%20zolpidem%20sleep%20aid%20tablets%20in%20pharmaceutical%20packaging%20on%20clean%20white%20surface%20medical%20product%20photography%20soft%20lighting%20high%20quality%20shot&width=300&height=300&seq=7&orientation=squarish',
      link: '/products/zolpidem-10mg'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="text-2xl font-bold text-teal-600" style={{ fontFamily: '"Pacifico", serif' }}>MediCare</Link>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Home</Link>
              <Link to="/all-products" className="text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Products</Link>
              <a href="/#contact" className="text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Contact</a>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/cart" className="w-10 h-10 flex items-center justify-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer relative">
                <i className="ri-shopping-cart-line text-xl"></i>
                <span className="absolute -top-1 -right-1 bg-teal-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{cartCount}</span>
              </Link>
              <Link to="/auth" className="w-10 h-10 flex items-center justify-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
                <i className="ri-user-line text-xl"></i>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Product Detail */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <div>
            <img src={product.image} alt={product.name} className="w-full rounded-2xl shadow-lg object-cover object-top" />
          </div>
          <div>
            <div className="flex items-center gap-3 mb-2">
              <p className="text-sm text-teal-600 font-semibold">{product.category}</p>
              <span className="px-3 py-1 bg-orange-100 text-orange-600 text-xs font-semibold rounded-full">
                {shippingType === 'overseas' ? 'Overseas Shipping' : 'US to US Shipping'}
              </span>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{product.name}</h1>
            <div className="flex items-center mb-6">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className={`${i < Math.floor(product.rating) ? 'ri-star-fill' : 'ri-star-line'} text-yellow-400 text-lg`}></i>
                ))}
              </div>
              <span className="ml-2 text-gray-600">({product.reviews} reviews)</span>
            </div>
            <p className="text-4xl font-bold text-gray-900 mb-6">
              ${currentPackages[selectedPackage as keyof typeof currentPackages]?.price || currentPackages[Object.keys(currentPackages)[0] as keyof typeof currentPackages].price}
            </p>
            
            <div className="mb-6">
              <h3 className="font-semibold text-gray-900 mb-3">Select Shipping Type</h3>
              <div className="grid grid-cols-2 gap-3 mb-6">
                <button
                  onClick={() => setShippingType('overseas')}
                  className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                    shippingType === 'overseas'
                      ? 'border-teal-600 bg-teal-50'
                      : 'border-gray-200 hover:border-teal-300'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <i className="ri-ship-line text-xl"></i>
                    <span className="font-semibold text-gray-900">Overseas</span>
                  </div>
                </button>
                <button
                  onClick={() => setShippingType('us')}
                  className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                    shippingType === 'us'
                      ? 'border-teal-600 bg-teal-50'
                      : 'border-gray-200 hover:border-teal-300'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <i className="ri-truck-line text-xl"></i>
                    <span className="font-semibold text-gray-900">US to US</span>
                  </div>
                </button>
              </div>

              <h3 className="font-semibold text-gray-900 mb-3">Select Package</h3>
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(currentPackages).map(([key, pkg]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedPackage(key)}
                    className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                      selectedPackage === key
                        ? 'border-teal-600 bg-teal-50'
                        : 'border-gray-200 hover:border-teal-300'
                    }`}
                  >
                    <div className="text-lg font-bold text-gray-900">{pkg.pills} pills</div>
                    <div className="text-sm text-teal-600 font-semibold">${pkg.price}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="border-t pt-6 mb-6">
              <p className="text-sm text-gray-600"><strong>SKU:</strong> MED{product.id.toString().padStart(6, '0')}</p>
            </div>

            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center border border-gray-300 rounded-lg">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="px-4 py-2 hover:bg-gray-100 cursor-pointer">
                  <i className="ri-subtract-line"></i>
                </button>
                <span className="px-6 py-2 border-x border-gray-300">{quantity}</span>
                <button onClick={() => setQuantity(quantity + 1)} className="px-4 py-2 hover:bg-gray-100 cursor-pointer">
                  <i className="ri-add-line"></i>
                </button>
              </div>
              <button onClick={addToCart} className="flex-1 bg-teal-600 text-white px-8 py-3 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer font-semibold">
                <i className="ri-shopping-cart-line mr-2"></i>
                Add to Cart
              </button>
            </div>

            <div className="flex items-center gap-4 text-sm text-gray-600 mb-6">
              <div className="flex items-center">
                <i className="ri-shield-check-line text-teal-600 mr-2"></i>
                <span>100% Genuine</span>
              </div>
              <div className="flex items-center">
                <i className="ri-truck-line text-teal-600 mr-2"></i>
                <span>Fast Shipping</span>
              </div>
              <div className="flex items-center">
                <i className="ri-lock-line text-teal-600 mr-2"></i>
                <span>Secure Payment</span>
              </div>
            </div>

            <p className="text-gray-600 mb-6">{product.description}</p>

            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Product Information</h3>
              <ul className="space-y-2">
                <li className="flex items-center">
                  <i className="ri-check-line text-teal-600 mr-2"></i>
                  <span className="text-gray-700"><strong>Active Ingredient:</strong> {product.activeIngredient}</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-teal-600 mr-2"></i>
                  <span className="text-gray-700"><strong>Manufacturer:</strong> {product.manufacturer}</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-teal-600 mr-2"></i>
                  <span className="text-gray-700"><strong>Dosage:</strong> {product.dosage}</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-teal-600 mr-2"></i>
                  <span className="text-gray-700"><strong>Packaging:</strong> {product.packaging}</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-truck-line text-teal-600 mr-2"></i>
                  <span className="text-gray-700"><strong>Shipping:</strong> {shippingType === 'overseas' ? 'Overseas' : 'US to US'}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: '"Pacifico", serif' }}>MediCare</h3>
              <p className="text-gray-400">Your trusted partner in healthcare.</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="hover:text-teal-400 transition-colors cursor-pointer">Home</Link></li>
                <li><Link to="/all-products" className="hover:text-teal-400 transition-colors cursor-pointer">Products</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Categories</h4>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-teal-400 transition-colors cursor-pointer">Pain Relief</a></li>
                <li><a href="#" className="hover:text-teal-400 transition-colors cursor-pointer">Sleep Aid</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Contact Info</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <i className="ri-phone-line mt-1 mr-2"></i>
                  <span>+1 (929) 268-8770</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-mail-line mt-1 mr-2"></i>
                  <span>sam4104t@gmail.com</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-400">&copy; 2024 MediCare. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-teal-400 transition-colors cursor-pointer">Powered by Readdy</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
